import { useEffect, useState } from "react";
import { SearchBox } from "../../../components/Search";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { BrushStart, ChartType } from "../../../models/chart_model";
import { ExchangeColors } from "../../Dashboard/Exchange3/FormatData";
import { getHaryanaMonthlyForecast } from "../../../Rest_api/RestApi";
import { HarayanaMonthlyForecastData } from "../../../models/rajasthan";
import Loading from "../../../components/Loading";

const chartSuffixes = [
  { suffix: "demand", displayName: "Demand", stackSuffix: "demand" },
  { suffix: "average", displayName: "Average", stackSuffix: "average" },
];

const selectedChartType = [
  {
    value: ChartType.Bar,
    label: "Standard Bar",
    stackId: undefined,
  },
  { value: ChartType.Line, label: "Line", stackId: undefined },
];

const MonthlyForecast = () => {
  const [chartData, setChartData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [selectedSuffix, setSelectedSuffix] = useState([
    chartSuffixes[0],
    chartSuffixes[1],
  ]);

  const [data, setData] = useState<HarayanaMonthlyForecastData[]>([]);
  const [loading, setLoading] = useState(false);

  const [selectedMonth, setSelectedMonth] = useState<
    { value: string; label: string }[]
  >([] as any);

  async function _handlefetchData() {
    setLoading(true);
    try {
      const startDate = "2025-01-01"; 
      const endDate = "2025-12-31";   
      const newData = await getHaryanaMonthlyForecast(startDate, endDate);
      setData(newData);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }

  const getDynamicMonths = () => {
    if (!data || data.length === 0) return [];

    const uniqueMonths = Array.from(new Set(data.map((item) => item.month)));

    return uniqueMonths
      .map((month) => ({
        value: String(month).toUpperCase().replace(' ', '-'), // Convert "Jan 25" to "JAN-25"
        label: String(month), 
      }))
      .sort((a, b) => {
        // Extract month and year for proper sorting
        const [monthA, yearA] = a.label.split(' ');
        const [monthB, yearB] = b.label.split(' ');
        
        if (yearA !== yearB) {
          return yearA.localeCompare(yearB);
        }
        
        const monthOrder = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        return monthOrder.indexOf(monthA) - monthOrder.indexOf(monthB);
      });
  };

  useEffect(() => {
    if (data.length > 0 && selectedMonth.length === 0) {
      const dynamicMonths = getDynamicMonths();
      setSelectedMonth(dynamicMonths.slice(0, 8)); 
    }
  }, [data]);

  useEffect(() => {
    _handlefetchData();
  }, []);

  const createChartObject = (
    key: string,
    index: number,
    suffix: string,
    displayName: string,
    stackSuffix: string,
    ExchangeColors: string[],
    type?: string
  ) => {
    return {
      dataKey: `${key}_${suffix}_${"MW".toLowerCase()}`,
      name: displayName === "Demand" ? "Peak" : displayName,
      legendColor: undefined,
      stroke: displayName === "Demand" ? "#34656D" : ExchangeColors[10],
      type:
        displayName === "Demand"
          ? selectedChartType[0].value
          : selectedChartType[1].value,
      stackId:
        selectedChartType[index].stackId !== undefined
          ? stackSuffix
          : undefined,
    };
  };

  useEffect(() => {
    let filteredData = data;

    if (selectedMonth.length > 0) {
      filteredData = data.filter((item: HarayanaMonthlyForecastData) => {
        const itemMonthValue = String(item.month).toUpperCase().replace(' ', '-');
        
        return selectedMonth.some(
          (date: { value: string }) => date.value === itemMonthValue
        );
      });
    }

    const processedData = filteredData.map(item => ({
      ...item,
      month_year: item.month 
    }));

    const par: ChartDataParams = {
      keys: ["month_year"],
      groupBy: [],
      volumeKey: ``,
      aggregationKeys: ["demand_mw", "average_mw"],
      priceKey: "",
      data: processedData,
    };
    
    const cData = FormatChartData(par);
    setChartData(cData.data);
    setChartKeys(cData.keys);
  }, [data, selectedMonth]);

  return (
    <>
      {loading && <Loading />}
      <div className="dash-card col-span-1 md:col-span-6  h-full w-full">
        <div className="flex justify-between items-center mb-2">
          <div className="gas-heading mb-4">
            <h2 className="chartHeading">Monthly Demand Forecast</h2>
          </div>

          <div className="w-[50%] ">
            <SearchBox
              options={getDynamicMonths()}
              isMany={true}
              placeholder="Search Month"
              selectedOptions={selectedMonth}
              onChange={(item: any) => {
                setSelectedMonth(
                  item.map(
                    (opt: any) =>
                      getDynamicMonths().find(
                        (date) => date.value === opt.value
                      ) ?? opt
                  )
                );
              }}
            />
          </div>
        </div>

        <ReMixChart
          data={chartData}
          showBrush={chartData.length > 96 * 7 ? true : undefined}
          brushStart={BrushStart.Start}
          brushIndex={
            chartData.length > 96 * 7
              ? { startIndex: 0, endIndex: 96 * 7 }
              : undefined
          }
          isTimeSlot={false}
          onlyTitle={false}
          barCategoryGap={10}
          unit={"MW"}
          yAxisLabel={"MW"}
          legends={selectedSuffix.flatMap(
            ({ suffix, displayName, stackSuffix }) =>
              chartKeys.map(
                (key, index) =>
                  createChartObject(
                    key,
                    index,
                    suffix,
                    displayName,
                    stackSuffix,
                    ExchangeColors
                  ) as any
              )
          )}
          xDataKey={"month_year"}
          xTick={undefined}
          secondXDataKey={undefined}
        />
      </div>
    </>
  );
};

export default MonthlyForecast;